import { useCallback, useContext, useEffect, useState } from 'react';
import GoogleOneTap from './index';
import { verifyGoogleIdToken, createGoogleUserSession } from './verifyToken';
import CookieService from '../../utils/miscUtils/CookieService';
import type { GoogleOneTapCredentialResponse, GoogleAuthResult } from './types';
import { trackEvent } from 'services/Gtm/functions';
import type { PageType } from 'interfaces/content/articles/Post';
import useBreakpoint from 'hooks/useBreakpoint';
import geoIp from 'paymentApi/geoIp';
import { UserContext } from 'providers/UserProvider';

interface GoogleOneTapLoginProps {
  clientId: string;
  onSuccess?: (userData: GoogleAuthResult) => void;
  onError?: (error: string) => void;
  pageType: PageType;
}

const GoogleOneTapLogin: React.FC<GoogleOneTapLoginProps> = ({ clientId, onSuccess, onError, pageType }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const isMobile = useBreakpoint('mdMax');
  const isTablet = useBreakpoint('lgMax');

  const { userVariants } = useContext(UserContext);
  const variants = userVariants?.googleOneTap as { variant: string };
  const variantToUse = variants?.variant;
  const oneTapEnabled = variantToUse !== 'off';

  let deviceType;
  if (isMobile) {
    deviceType = 'mobile';
  } else if (isTablet) {
    deviceType = 'tablet';
  } else {
    deviceType = 'desktop';
  }

  useEffect(() => {
    // Check if user is already logged in by checking Piano session cookies
    const utpCookie = CookieService.get('__utp');
    setIsLoggedIn(!!utpCookie);
  }, []);

  const handleCredentialResponse = useCallback(
    async (response: GoogleOneTapCredentialResponse) => {
      if (isProcessing) return; // Prevent double processing

      setIsProcessing(true);

      try {
        // Step 1: Verify the Google ID token
        const verificationResult = await verifyGoogleIdToken(response.credential);

        if (!verificationResult.success) {
          trackEvent({
            eventLabel: 'google_one_tap_login_error',
            eventAction: 'error/drop-off',
            customData: {
              pageType,
              deviceType,
              user_registration_type: 'google_one_tap',
              error: 'token_verification_failed',
            },
          });
          onError?.(verificationResult.error || 'Token verification failed');
          return;
        }
        trackEvent({
          eventLabel: 'google_one_tap_credential_received',
          eventAction: 'click/engagement',
          customData: {
            pageType,
            deviceType,
            user_registration_type: 'google_one_tap',
          },
        });

        // Step 2: Create user session with Piano API
        if (!verificationResult.payload) {
          trackEvent({
            eventLabel: 'google_one_tap_login_error',
            eventAction: 'error/drop-off',
            customData: {
              pageType,
              deviceType,
              user_registration_type: 'google_one_tap',
              error: 'invalid_token_payload',
            },
          });
          onError?.('Invalid token payload');
          return;
        }

        const sessionResult = await createGoogleUserSession(verificationResult.payload);

        if (!sessionResult.success) {
          trackEvent({
            eventLabel: 'google_one_tap_login_error',
            eventAction: 'error/drop-off',
            customData: {
              pageType,
              deviceType,
              user_registration_type: 'google_one_tap',
              error: 'session_creation_failed',
            },
          });
          onError?.(sessionResult.error || 'Session creation failed');
          return;
        }

        // Step 3: Set Piano session cookies
        if (sessionResult.pianoToken && sessionResult.pianoUid) {
          CookieService.set('__utp', sessionResult.pianoToken, {
            secure: true,
            sameSite: 'strict',
          });
        }

        // Step 4: Success - user is authenticated and session is created
        const successData: GoogleAuthResult = {
          success: true,
          pianoUid: sessionResult.pianoUid,
          accessToken: sessionResult.pianoToken,
          isNewUser: sessionResult.isNewUser,
          message: sessionResult.message,
        };
        trackEvent({
          eventLabel: 'google_one_tap_login_success',
          eventAction: 'conversion',
          customData: {
            pageType,
            deviceType,
            isNewUser: JSON.stringify(sessionResult.isNewUser),
            user_registration_type: 'google_one_tap',
          },
        });

        if (sessionResult.isNewUser) {
          trackEvent({
            eventLabel: 'google_one_tap_user_created',
            eventAction: 'conversion',
            customData: {
              pageType,
              deviceType,
              isNewUser: JSON.stringify(sessionResult.isNewUser),
              user_registration_type: 'google_one_tap',
            },
          });
          // Set user's locale info
          await geoIp(successData.pianoUid, false);
        }

        onSuccess?.(successData);

        // Step 5: Reload the page to reflect the new authentication state
        window.location.reload();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to process Google One Tap response';
        onError?.(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    },
    [isProcessing, onSuccess, onError],
  );

  function momentCallback(notification) {
    if (notification.getNotDisplayedReason?.() === 'suppressed_by_user') {
      trackEvent({
        eventLabel: 'google_one_tap_prompt_dismissed',
        eventAction: 'click/engagement',
        customData: {
          pageType,
          deviceType,
          user_registration_type: 'google_one_tap',
        },
      });
    }

    if (notification.isDisplayed?.()) {
      trackEvent({
        eventLabel: 'google_one_tap_prompt_shown',
        eventAction: 'impression',
        customData: {
          pageType,
          deviceType,
          user_registration_type: 'google_one_tap',
        },
      });
    }

    if (notification.getSkippedReason?.() === 'tap_outside') {
      trackEvent({
        eventLabel: 'google_one_tap_prompt_dismissed',
        eventAction: 'click outside/engagement',
        customData: {
          pageType,
          deviceType,
          user_registration_type: 'google_one_tap',
        },
      });
    }
  }

  // Don't render Google One Tap if user is already logged in
  if (isLoggedIn === null) {
    // Still checking login status, don't render anything yet
    return null;
  }

  if (isLoggedIn) {
    // User is already logged in, don't show Google One Tap
    return null;
  }

  return (
    oneTapEnabled && (
      <GoogleOneTap
        clientId={clientId}
        callback={handleCredentialResponse}
        context='signin'
        cancelOnTapOutside={true}
        autoSelect={false}
        momentCallback={momentCallback}
      />
    )
  );
};

export default GoogleOneTapLogin;
